import { BILLABLE_STATUSES, JOB_TYPES } from "@/lib/constants";
import { Tooltip, TooltipContent, TooltipTrigger } from "ui/components/tooltip";
import { differenceInDays, parseISO } from "date-fns";

import { Avatar } from "ui/components/avatar";
import { Badge } from "ui/components/badge";
import { DisplayAmount } from "components/display/amount";
import { Icon } from "components";
import { Link } from "react-router-dom";
import { RenderAvatar } from "components/render-avatar";
import {
  formatDateForDisplay,
  getBillableExtensionDays,
  ROUTE,
} from "@/lib/utils";
import { maskPrivacyAmount } from "utils/utils";
import { usePrivacy } from "contexts/privacy";

export const StatusCell = ({ getValue }) => {
  const status = BILLABLE_STATUSES[getValue()];
  if (!status) return null;
  return <Badge className={status.className}>{status.label}</Badge>;
};

export const TypeCell = ({ getValue }) => {
  return (
    <Tooltip>
      <TooltipTrigger>
        <Icon
          icon={JOB_TYPES[getValue()].icon}
          className="text-muted-foreground size-4"
        />
      </TooltipTrigger>
      <TooltipContent>{JOB_TYPES[getValue()].label}</TooltipContent>
    </Tooltip>
  );
};

export const AmountCell = ({ row }) => {
  const { privacyOn } = usePrivacy();
  let { amount, currency } = row.original;
  const currencyName = currency?.name || currency || "";
  amount = Number(amount);
  if (!amount) return null;
  return (
    <Tooltip>
      <TooltipTrigger>
        <div>
          {privacyOn ? (
            maskPrivacyAmount(amount)
          ) : (
            <>
              <DisplayAmount value={amount} />{" "}
              {currencyName && `${currencyName}`}
            </>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        {privacyOn
          ? maskPrivacyAmount(amount)
          : `Amount: ${amount} ${currencyName}`}
      </TooltipContent>
    </Tooltip>
  );
};

export const AmountCell2 = ({ getValue }) => {
  if (!getValue() && getValue() !== 0) return null;

  return <DisplayAmount value={getValue()} />;
};

export const AdjustedAmountCell = ({ row }) => {
  const { privacyOn } = usePrivacy();
  let { adjusted_amount, currency } = row.original;
  const currencyName = currency?.name || currency || "";
  adjusted_amount = Number(adjusted_amount);
  if (!adjusted_amount) return null;
  return (
    <div>
      {privacyOn ? (
        maskPrivacyAmount(adjusted_amount)
      ) : (
        <>
          <DisplayAmount value={adjusted_amount} />{" "}
          {currencyName && `${currencyName}`}
        </>
      )}
    </div>
  );
};

export const ProjectCell = (info) => {
  const project = info.getValue();
  const projectId = info.row.original.job.project.project_id;
  if (!project) return null;
  return (
    <Link to={ROUTE.PROJECT(projectId)} target="_blank">
      <div className="hover:underline">{project}</div>
    </Link>
  );
};

export const JobCell = (info) => {
  const { getValue, row } = info;

  const jobId = row.original.job.id;

  return (
    <Link to={ROUTE.JOB(jobId)} target="_blank">
      <div className="hover:underline">{getValue()}</div>
    </Link>
  );
};

export const NameCell = ({ row }) => {
  const name = row.getValue("name");
  const extensionDays = getBillableExtensionDays(row.original);

  return (
    <div className="space-y-1" title={name}>
      <Link to={`${ROUTE.BILLABLE(row.original.id)}/edit`} target="_blank">
        <div className="hover:underline">{name ?? "__"}</div>
      </Link>
      {extensionDays ? (
        <div className="text-red-600">Extended by {extensionDays} days</div>
      ) : null}
    </div>
  );
};

export const ExtendedNameCell = ({
  row,
  href = `${ROUTE.BILLABLE(row.original.id)}/edit`,
}) => {
  const name = row.getValue("name");
  const extensionDays = getBillableExtensionDays(row.original);

  return (
    <div className="space-y-1" title={name}>
      <Link to={href} target="_blank">
        <div className="hover:underline">{name ?? "__"}</div>
      </Link>
      {extensionDays ? (
        <div className="text-red-600">
          Extended by {extensionDays} days last extended on{" "}
          {formatDateForDisplay(
            row.original.extensions[row.original.extensions.length - 1]
              .date_created,
          )}
        </div>
      ) : null}
    </div>
  );
};

export const BillableNameCell = ({ row }) => {
  const name = row.getValue("name");

  return (
    <Link to={`${ROUTE.BILLABLE(row.original.id)}`} target="_blank">
      <div className="hover:underline">{name ?? "__"}</div>
    </Link>
  );
};

export const DateCell = (info) =>
  formatDateForDisplay(info.getValue(), { addSuffix: true });
